import React from "react";
import {
  GridActionsColDef,
  GridLinkOperator,
  GridRenderCellParams,
  GridRowParams,
  renderActionsCell,
} from "@mui/x-data-grid-pro";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Link from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Link as RouterLink } from "react-router-dom";
import parseDate from "date-fns/parse";
import startOfMonth from "date-fns/startOfMonth";
import { filterModelToSearchParams } from "common/graphql/queryString";
import { CurbsuiteTransactionLogEntry } from "common/graphql/queries/GetCurbsuiteTransactions";
import { ACTIONS_COLUMN_ICON, ACTIONS_VIEW_ICON_SX, DataGridActionsLinkButton } from "../../../DataTableContainer";
import {
  ADJUSTMENT_TYPE_LABEL,
  formatReferenceTransactionIndex,
  getAdjustmentDescription,
  isAdjustment,
} from "../../common";

type AdjustmentLinkRow = Pick<
  CurbsuiteTransactionLogEntry,
  | "transactionType"
  | "referenceTransactionId"
  | "referenceTransactionIndex"
  | "referenceTransactionType"
  | "referencedTransaction"
>;

/**
 * The first of the referenced charge's month, on the date field the month picker filters this tab
 * by (useTransactionsUrlState: `assigned_to_account_at_local_date` on the bus-station park tab,
 * `transaction_date` everywhere else), or null when the reference's row didn't come back.
 */
function getReferenceMonth(row: AdjustmentLinkRow, isBusStationPark: boolean): Date | null {
  const reference = row.referencedTransaction;
  const localDate = isBusStationPark
    ? reference?.assignedToAccountAtLocalDate ?? reference?.transactionDate
    : reference?.transactionDate;
  if (!localDate) {
    return null;
  }
  // A `yyyy-MM-dd` local date: parse it in local time -- `new Date("2026-08-01")` is UTC midnight,
  // which is the previous month in US time zones.
  const parsed = parseDate(localDate, "yyyy-MM-dd", new Date());
  return Number.isNaN(parsed.getTime()) ? null : startOfMonth(parsed);
}

/**
 * Where an adjustment row's "Adjusts #<index>" link goes (AUT-6163), as a `?search` string on the
 * current route, or null for a row that isn't an adjustment / has no reference.
 *
 * - A `park` reference has a detail page: the existing `?transactionId=<id>` view.
 * - A `permit` / `wallet_reload` reference has none, so the link filters this same table to the
 *   referenced charge's ID and moves the month picker to the referenced charge's month (read through
 *   the `referenced_transaction` relationship) -- a chargeback usually lands in a later month than the
 *   charge it reverses. The referenced charge always lives on the same tab as its adjustment (see
 *   buildCurbsuiteBaseFilter), so the `tab` param is kept as is.
 *
 * Built from a copy of `searchParams` -- this runs per row on every render.
 */
export function buildAdjustmentReferenceSearch(
  row: AdjustmentLinkRow,
  searchParams: URLSearchParams,
  { isBusStationPark = false }: { isBusStationPark?: boolean } = {},
): string | null {
  if (!isAdjustment(row) || !row.referenceTransactionId) {
    return null;
  }
  if (row.referenceTransactionType === "park") {
    const params = new URLSearchParams(searchParams);
    params.set("transactionId", row.referenceTransactionId);
    return `?${params.toString()}`;
  }
  const referenceIndex = formatReferenceTransactionIndex(row);
  if (!referenceIndex) {
    return null;
  }
  const params = filterModelToSearchParams(
    {
      items: [{ id: "1", columnField: "transactionIndex", operatorValue: "equals", value: referenceIndex }],
      linkOperator: GridLinkOperator.And,
    },
    searchParams,
  );
  const referenceMonth = getReferenceMonth(row, isBusStationPark);
  if (referenceMonth) {
    params.set("date", referenceMonth.toISOString());
  }
  params.delete("transactionId");
  params.set("page", "1");
  return `?${params.toString()}`;
}

/**
 * The two-line cell an adjustment row shows where its type is (AUT-6163): "Adjustment", over a
 * smaller "Adjusts #<index> · <description>" whose "#<index>" links to the charge it adjusts. This
 * is how PPA sees *that* a charge was reversed and why; the row's own Date column carries *when*.
 *
 * The link comes first so a long description can't push it out of the narrow cell; the description
 * is truncated, with the full text on hover -- the same layout as CurbPass's Transaction Type cell.
 */
export const AdjustmentTypeCell: React.FC<{
  row: CurbsuiteTransactionLogEntry;
  searchParams: URLSearchParams;
  isBusStationPark?: boolean;
}> = ({ row, searchParams, isBusStationPark = false }) => {
  const description = getAdjustmentDescription(row);
  const referenceIndex = formatReferenceTransactionIndex(row);
  const search = buildAdjustmentReferenceSearch(row, searchParams, { isBusStationPark });
  return (
    <Tooltip title={description ?? ""} disableHoverListener={!description}>
      <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, py: 0.5 }}>
        <Typography variant={"body2"}>{ADJUSTMENT_TYPE_LABEL}</Typography>
        {(description || referenceIndex) && (
          <Typography variant={"caption"} color={"text.secondary"} noWrap>
            {referenceIndex &&
              (search ? (
                <>
                  {"Adjusts "}
                  <Link component={RouterLink} to={search} replace underline={"hover"}>
                    {`#${referenceIndex}`}
                  </Link>
                </>
              ) : (
                `Adjusts #${referenceIndex}`
              ))}
            {description && referenceIndex ? " · " : null}
            {description}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
};

/**
 * `renderCell` for a Type column: the two-line AdjustmentTypeCell on adjustment rows, and the plain
 * formatted value (the column's own label) on every other row. The cell *value* stays the bare
 * label, so the singleSelect "equals" filter and the CSV still see "Adjustment".
 */
export function renderTypeCellWithAdjustment(
  searchParams: URLSearchParams,
  { isBusStationPark = false }: { isBusStationPark?: boolean } = {},
) {
  return (params: GridRenderCellParams<string, CurbsuiteTransactionLogEntry, string>): React.ReactNode =>
    isAdjustment(params.row) ? (
      <AdjustmentTypeCell row={params.row} searchParams={searchParams} isBusStationPark={isBusStationPark} />
    ) : (
      params.formattedValue ?? params.value
    );
}

/** The chevron action for an adjustment row: the same destination as its "Adjusts #" link. */
export function buildAdjustmentReferenceActions(
  row: CurbsuiteTransactionLogEntry,
  searchParams: URLSearchParams,
  { isBusStationPark = false }: { isBusStationPark?: boolean } = {},
): JSX.Element[] {
  const search = buildAdjustmentReferenceSearch(row, searchParams, { isBusStationPark });
  if (!search) {
    return [];
  }
  return [
    <DataGridActionsLinkButton
      key={"view-adjusted-transaction"}
      to={search}
      replace
      label={"View Adjusted Transaction"}
      icon={<ArrowForwardIosIcon sx={ACTIONS_VIEW_ICON_SX} />}
    />,
  ];
}

/** Width of an actions column that also carries an adjustment's two-line Type cell. */
export const ADJUSTMENT_ACTIONS_COLUMN_WIDTH = 220;

/**
 * The actions column for a table with no on-screen Type column -- standard park, and the airport
 * "Monthly Charges" wallet tab -- which is where an adjustment row's two-line AdjustmentTypeCell goes
 * instead (no new column). Every other row keeps its usual actions (`getActions`). The column is
 * widened to fit the two lines; it is `type: "actions"`, so the CSV still skips it.
 */
export function buildActionsColumnWithAdjustment({
  searchParams,
  getActions,
}: {
  searchParams: URLSearchParams;
  getActions: (params: GridRowParams<CurbsuiteTransactionLogEntry>) => JSX.Element[];
}): GridActionsColDef {
  return {
    ...ACTIONS_COLUMN_ICON,
    width: ADJUSTMENT_ACTIONS_COLUMN_WIDTH,
    align: "left",
    getActions: (params) =>
      isAdjustment(params.row as CurbsuiteTransactionLogEntry)
        ? []
        : getActions(params as GridRowParams<CurbsuiteTransactionLogEntry>),
    renderCell: (params) =>
      isAdjustment(params.row as CurbsuiteTransactionLogEntry) ? (
        <AdjustmentTypeCell row={params.row as CurbsuiteTransactionLogEntry} searchParams={searchParams} />
      ) : (
        renderActionsCell(params)
      ),
  } as GridActionsColDef;
}

/**
 * Actions column for the standard wallet variant, which had none (AUT-6163): gateway rows have no
 * detail page, so only adjustment rows get a chevron -- to the charge they adjust.
 */
export function buildWalletActionsColumn({ searchParams }: { searchParams: URLSearchParams }): GridActionsColDef {
  return {
    ...ACTIONS_COLUMN_ICON,
    getActions: (params) => buildAdjustmentReferenceActions(params.row as CurbsuiteTransactionLogEntry, searchParams),
  } as GridActionsColDef;
}
